import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import {
  Controller,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { textService } from "../../../core/services/textService";

export interface AddressFormData extends FieldValues {
  address: {
    postalCode?: number;
    city?: string;
    streetAddress?: string;
  };
}

type AddressProps<T extends AddressFormData> = {
  form: UseFormReturn<T, any, T>;
  required: boolean;
};

export function AddressField<T extends AddressFormData>({
  form,
  required,
}: AddressProps<T>) {
  const { t } = useTranslation();

  const addressErrors = form.formState.errors.address as
    | Record<string, any>
    | undefined;

  return (
    <div className="grid">
      <div className="col-3 pl-0 pr-0">
        <Controller
          name={"address.postalCode" as Path<T>}
          control={form.control}
          rules={{
            required: required && t("Error.Required"),
            validate: (value) =>
              !required ||
              !!value?.toString().match(/^\d{4}$/) ||
              t("Error.Pattern"),
          }}
          render={({ field }) => (
            <InputNumber
              mode="decimal"
              useGrouping={false}
              className="w100"
              inputClassName="w100"
              invalid={!!addressErrors?.postalCode}
              max={9999}
              maxLength={4}
              value={field.value}
              onChange={(e) => field.onChange(e.value ?? undefined)}
              onBlur={field.onBlur}
            />
          )}
        />
      </div>
      <div className="col-9">
        <InputText
          className="w100"
          {...form.register("address.city" as Path<T>, {
            required: required && t("Error.Required"),
            minLength: {
              value: required ? 3 : 0,
              message: t("Error.MinLength", { value: 3 }),
            },
            onChange: (e) => {
              e.target.value = textService.capitalize(e.target.value);
              return e;
            },
          })}
          invalid={!!addressErrors?.city}
        />
      </div>
      <div className="col-12 pl-0 pt-1">
        <InputText
          className="w100"
          {...form.register("address.streetAddress" as Path<T>, {
            required: required && t("Error.Required"),
            minLength: {
              value: required ? 3 : 0,
              message: t("Error.MinLength", { value: 3 }),
            },
            onChange: (e) => {
              e.target.value = textService.capitalize(e.target.value);
              return e;
            },
          })}
          invalid={!!addressErrors?.streetAddress}
        />
      </div>

      {(addressErrors?.postalCode ||
        addressErrors?.city ||
        addressErrors?.streetAddress) && (
        <small className="p-error block">
          {addressErrors.postalCode?.message ||
            addressErrors.city?.message ||
            addressErrors.streetAddress?.message}
        </small>
      )}
    </div>
  );
}
